import { Input } from '@chakra-ui/react'
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import profileIcon from '../../assets/icons/profile-icon.svg'
import circle from '../../assets/icons/Circle.png'
import { showToast } from '../../utils/toast'
import FormInput from '../Input'
import Dropdown from '../Dropdown'
import ArtisanService from '../../api/artisan-services'
import { CreateArtisanReq, EditArtisanReq } from '../../interfaces/services'
import Loader from '../../utils/Loader'

type ServiceOption = {
  id: string
  title: string
}

type AddArtisanFormProps = {
  defaultRole?: string
  serviceId?: string
  estateId?: string
  initialServiceOptions?: ServiceOption[]
  onCancel?: () => void
  onContinue?: (data: any) => Promise<void> | void
  artisanId?: string | null
  mutate: () => void
}

const AddArtisan = forwardRef<{ resetForm: () => void }, AddArtisanFormProps>(
  (
    {
      defaultRole = 'Painter',
      serviceId,
      estateId,
      initialServiceOptions = [],
      onCancel,
      onContinue,
      artisanId,
      mutate,
    },
    ref
  ) => {
    const [photoLoading, setPhotoLoading] = useState(false)
    const [saveLoading, setSaveLoading] = useState(false)

    const [imageUrl, setImageUrl] = useState<string | null>(null)

    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null)

    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [role, setRole] = useState(defaultRole)
    const [fullName, setFullName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [address, setAddress] = useState('')
    const [dateOfBirth, setDateOfBirth] = useState('')
    const [gender, setGender] = useState('Male')
    const [nationality, setNationality] = useState('Nigerian')
    const [loading, setLoading] = useState(false)
    const [serviceOptions, setServiceOptions] =
      useState<ServiceOption[]>(initialServiceOptions)
    const [selectedServices, setSelectedServices] = useState<string[]>(() =>
      serviceId ? [serviceId] : []
    )
    const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false)
    const serviceDropdownRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
      async function fetchArtisan() {
        if (!artisanId) return
        setLoading(true)
        try {
          const artisan = await ArtisanService.getArtisanById(artisanId)
          setFullName(artisan.fullName || '')
          setEmail(artisan.email || '')
          setPhone(artisan.phone || '')
          setAddress(artisan.address || '')
          setDateOfBirth(artisan.dob?.slice(0, 10) || '')
          if (artisan.imageUrl) {
            setImageUrl(artisan.imageUrl)
            setUploadedImageUrl(artisan.imageUrl)
          }
          if (artisan.serviceIds && Array.isArray(artisan.serviceIds)) {
            setSelectedServices(artisan.serviceIds)
          }
        } catch (err) {
          showToast({
            title: 'Failed to Load Artisan',
            description: 'Could not fetch artisan details.',
            status: 'error',
          })
        } finally {
          setLoading(false)
        }
      }
      fetchArtisan()
    }, [artisanId])

    useEffect(() => {
      function onDocClick(e: MouseEvent) {
        if (!serviceDropdownRef.current) return
        if (!serviceDropdownRef.current.contains(e.target as Node))
          setServiceDropdownOpen(false)
      }
      document.addEventListener('click', onDocClick)
      return () => document.removeEventListener('click', onDocClick)
    }, [])

    const genderOptions = [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Others', label: 'Others' },
    ]
    const nationalityOptions = [
      { value: 'Nigerian', label: 'Nigerian' },
      { value: 'Other', label: 'Other' },
    ]

    useImperativeHandle(ref, () => ({
      resetForm: () => {
        setRole(defaultRole)
        setFullName('')
        setEmail('')
        setPhone('')
        setAddress('')
        setDateOfBirth('')
        setGender('Male')
        setNationality('Nigerian')
        setImageUrl(null)
        setUploadedImageUrl(null)
        setSelectedServices(serviceId ? [serviceId] : [])
      },
    }))

    const shouldFetchServices = Boolean(estateId && estateId.trim().length > 0)
    const serviceListSWR = ArtisanService.useGetServiceList({
      estateId: shouldFetchServices ? estateId : undefined,
      page: 1,
      size: 200,
    })
    const servicesLoading = shouldFetchServices && Boolean(serviceListSWR?.isValidating)

    useEffect(() => {
      if (initialServiceOptions && initialServiceOptions.length > 0) {
        setServiceOptions(initialServiceOptions)
        return
      }
      if (!shouldFetchServices) return

      const raw = (serviceListSWR as any)?.data ?? null
      if (!raw) return

      const list: any[] = (raw as any).results ?? []
      setServiceOptions(
        list.map((s: any) => ({
          id: s.id ?? s._id ?? s.serviceId ?? String(s.id),
          title: s.title ?? s.name ?? s.type ?? 'Unknown',
        }))
      )
    }, [serviceListSWR?.data, estateId, initialServiceOptions, shouldFetchServices])

    const handleUploadClick = () => fileInputRef.current?.click()

    function isEmailValid(value: string) {
      return /\S+@\S+\.\S+/.test(value)
    }

    const isFormValid =
      !!role &&
      fullName.trim().length > 0 &&
      phone.trim().length > 0 &&
      address.trim().length > 0 &&
      dateOfBirth.trim().length > 0 &&
      gender.trim().length > 0 &&
      nationality.trim().length > 0 &&
      isEmailValid(email.trim()) &&
      selectedServices.length > 0

    async function handleSubmit(e: React.FormEvent) {
      e.preventDefault()

      if (saveLoading) return

      if (!isFormValid) {
        showToast({
          title: 'Invalid Form',
          description: 'Please fill required fields and select at least one service.',
          status: 'error',
          id: 'form-invalid',
        })
        return
      }

      setSaveLoading(true)

      try {
        let response

        if (artisanId) {
          const payload: Partial<EditArtisanReq> = {
            fullName: fullName.trim(),
            address: address.trim(),
            dob: dateOfBirth,
            serviceIds: selectedServices,
            ...(uploadedImageUrl && { imageUrl: uploadedImageUrl }),
          }
          response = await ArtisanService.updateArtisan(artisanId, payload)
          showToast({
            title: 'Updated Successfully',
            description: `Artisan updated with ${selectedServices.length} service(s).`,
            status: 'success',
            id: 'update-success',
          })
          mutate()
          onCancel?.()
        } else {
          const createPayload: CreateArtisanReq = {
            fullName: fullName.trim(),
            email: email.trim(),
            phone: phone.trim(),
            address: address.trim(),
            dob: dateOfBirth,
            serviceIds: selectedServices,
            imageUrl: uploadedImageUrl ?? undefined,
          }
          response = await ArtisanService.createArtisan(createPayload)
          showToast({
            title: 'Created Successfully',
            description: `Artisan created with ${selectedServices.length} service(s).`,
            status: 'success',
            id: 'create-success',
          })
          if (onContinue) {
            await onContinue({
              ...response,
              imageUrl: uploadedImageUrl ?? undefined,
            })
          }
        }
      } catch (err: any) {
        console.error(err)
        showToast({
          title: 'Failed',
          description: err?.message || 'Something went wrong.',
          status: 'error',
          id: 'submit-error',
        })
      } finally {
        setSaveLoading(false)
      }
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!validImageTypes.includes(file.type)) {
        showToast({
          title: 'Unsupported File Format',
          description: 'Please upload a JPEG, PNG, GIF or WebP image.',
          status: 'error',
        })
        return
      }

      const previewUrl = URL.createObjectURL(file)
      setImageUrl(previewUrl)
      setPhotoLoading(true)

      try {
        const res = await ArtisanService.uploadArtisanPhoto(file)
        const persistentUrl = res.data.url
        URL.revokeObjectURL(previewUrl)
        setImageUrl(persistentUrl)

        setUploadedImageUrl(persistentUrl)

        showToast({
          title: 'Upload Successful',
          description: 'Photo uploaded.',
          status: 'success',
        })
      } catch (err) {
        URL.revokeObjectURL(previewUrl)
        setImageUrl(null)
        setUploadedImageUrl(null)
        showToast({
          title: 'Upload Failed',
          description: 'Error uploading photo. Try again.',
          status: 'error',
        })
      } finally {
        setPhotoLoading(false)
      }
    }

    const formInputProps = {
      size: 'lg' as const,
      borderRadius: 'none' as const,
      className:
        'w-full rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500',
      labelColor: '#6C757D',
    }

    function toggleService(id: string) {
      setSelectedServices((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      )
    }

    const selectedTitles = serviceOptions
      .filter((s: ServiceOption) => selectedServices.includes(s.id))
      .map((s: ServiceOption) => s.title)

    const showEstateWarning = !estateId || estateId.trim().length === 0

    if (artisanId && loading) {
      return <Loader message="Loading artisan details..." />
    }

    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto">
          {showEstateWarning && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
              ⚠️ Estate ID is missing. Service list may not load properly.
            </div>
          )}

          <div className="mb-10">
            <label className="block text-sm text-gray-600 mb-2">
              Maintenance services
            </label>

            <div className="relative" ref={serviceDropdownRef}>
              <button
                type="button"
                onClick={() => setServiceDropdownOpen((s) => !s)}
                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-left flex justify-between items-center"
                aria-haspopup="listbox"
                aria-expanded={serviceDropdownOpen}
              >
                <div className="truncate">
                  {selectedTitles.length > 0
                    ? selectedTitles.join(', ')
                    : servicesLoading
                      ? 'Loading services...'
                      : 'Select services'}
                </div>
                <div className="text-gray-400">▾</div>
              </button>

              {serviceDropdownOpen && (
                <div
                  className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded shadow max-h-60 overflow-auto"
                  role="listbox"
                >
                  {serviceOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {servicesLoading ? 'Loading services...' : 'No services available'}
                    </div>
                  ) : (
                    serviceOptions.map((s: ServiceOption) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(s.id)}
                          onChange={() => toggleService(s.id)}
                          aria-checked={selectedServices.includes(s.id)}
                        />
                        <span className="text-sm truncate">{s.title}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:gap-10 gap-12 mb-6">
              <div className="flex flex-col gap-3 sm:items-center lg:items-start">
                <label className="text-[#6C757D] text-center">Artisan image</label>
                <div className="bg-[#E9ECEF] h-[170px] relative w-[160px] rounded-lg flex flex-col items-center justify-center self-center">
                  <img
                    src={circle}
                    alt="Upload"
                    className="absolute self-end top-0 right-0 cursor-pointer"
                    onClick={handleUploadClick}
                  />
                  <Input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                  <img
                    src={imageUrl ?? profileIcon}
                    alt="Artisan profile"
                    className="h-full w-full object-cover rounded-lg"
                  />
                </div>
              </div>

              {photoLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="w-[300px] h-[200px] bg-white rounded-lg shadow-lg flex flex-col items-center justify-center">
                    <p className="text-lg font-bold mb-4">Uploading Your Photo...</p>
                    <div className="h-10 w-10 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                  </div>
                </div>
              )}

              <div className="flex flex-col flex-1 gap-5 md:gap-10">
                <div className="flex gap-5 md:gap-10 flex-grow flex-col lg:flex-row">
                  <FormInput
                    placeholder="Enter Artisan's full name"
                    type="text"
                    label="Fullname"
                    name="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    {...formInputProps}
                  />
                  <FormInput
                    placeholder="Enter Artisan's email"
                    type="email"
                    label="Email address"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    {...formInputProps}
                    disabled={!!artisanId}
                  />
                </div>

                <div className="flex gap-5 md:gap-10 flex-col lg:flex-row">
                  <FormInput
                    placeholder="Enter Artisan's phone number"
                    type="text"
                    label="Phone number"
                    name="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    {...formInputProps}
                  />
                  <FormInput
                    placeholder="Enter Artisan's address"
                    type="text"
                    label="Address"
                    name="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    {...formInputProps}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-5 md:gap-8 mb-14 md:mb-20 flex-col lg:flex-row">
              <div className="space-y-2.5 lg:w-5/12">
                <FormInput
                  type="date"
                  name="dateOfBirth"
                  label="Date of Birth"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  {...formInputProps}
                  disabled={!!artisanId}
                />
              </div>
              <div className="space-y-2.5 lg:w-2/12">
                <p className="text-[#6C757D] font-medium text-lg leading-9">Gender</p>
                <Dropdown
                  options={genderOptions}
                  onSelect={(value: string) => setGender(value)}
                  defaultLabel="Select gender"
                  defaultValue={gender}
                  disabled={!!artisanId}
                />
              </div>
              <div className="space-y-2.5 lg:w-5/12">
                <p className="text-[#6C757D] font-medium text-lg leading-9">
                  Nationality
                </p>
                <Dropdown
                  options={nationalityOptions}
                  onSelect={(value: string) => setNationality(value)}
                  defaultLabel="Select nationality"
                  defaultValue={nationality}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={() => onCancel?.()}
                className="px-6 py-2 border border-gray-300 rounded"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!isFormValid || saveLoading || photoLoading}
                className={`px-6 py-2 rounded text-white ${
                  isFormValid && !saveLoading && !photoLoading
                    ? 'bg-black'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {saveLoading ? (artisanId ? 'Updating...' : 'Creating...') : artisanId ? 'Save' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
)

export default AddArtisan
